/**
 * Primary left navigation. Renders the {@link NAV_ITEMS} model grouped by
 * {@link NAV_SECTIONS}, hiding any entry the live session can't access (RBAC),
 * and marks the active route via TanStack Router's `useRouterState`.
 *
 * ## Why two sections fold
 *
 * Expanded, the rail is ~24 links, which a reader scans as a wall rather than a
 * list. Reference and Automate ({@link COLLAPSIBLE_NAV_SECTIONS}) are the
 * low-traffic halves of it — material you consult, and machinery that runs
 * without you — so they start folded and the rail opens at roughly fourteen
 * entries. Two rules keep the fold from hiding anything that matters: the
 * section holding the ACTIVE route is always expanded (never hide where the user
 * is standing), and the choice is remembered per IDENTITY rather than per
 * browser — display preferences stored app-wide are how one analyst's view came
 * to follow every other user of a shared workstation.
 *
 * ## Why the skip link is here
 *
 * The shell paints sidebar → header → main, and the sidebar is still a long list
 * of links. Without a skip target, reaching page content by keyboard meant
 * tabbing past all of them on every route change. The link has to be the FIRST
 * focusable thing in the document, and this `<aside>` is the first element of the
 * shell — so it lives here rather than in the header, which renders after it.
 *
 * ## Why active state is more than a class
 *
 * `bg-accent` is invisible to a screen reader, so the active item was announced
 * identically to every other one. `aria-current="page"` is what says which one
 * you are on.
 */
import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { usePermissions } from "@authe/ui-core/permissions";
import { useEntitled } from "@authe/ui-core";
import { cn } from "@authe/ui-core";
import { createScopedStorage, type ScopedStorage } from "@authe/fe-commons/storage";
import { useStorageIdentity } from "@/app/storageIdentity";
import { MAIN_CONTENT_ID } from "@/app/layoutIds";
import {
  APP_NAME,
  APP_TAGLINE,
  COLLAPSIBLE_NAV_SECTIONS,
  NAV_SECTIONS,
  type NavItem,
  type NavSection,
  visibleNavItems,
} from "@/config";

/** A section heading as a DOM id — `aria-labelledby` cannot reference one with spaces. */
function sectionId(section: string): string {
  return `nav-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

/**
 * Scoped-storage name for the folded sections.
 *
 * The VALUE is the list of collapsed section names, not of expanded ones, so
 * "absent" and "expanded" stay distinguishable: a reader who opens both sections
 * stores `[]`, which is a decision, while a reader who has never touched the
 * rail stores nothing and gets the default fold.
 */
const COLLAPSED_SECTIONS_KEY = "nav:collapsed-sections";

/**
 * The folded sections for this identity, defaulting to the two that start folded.
 *
 * Every stored name is re-checked against {@link COLLAPSIBLE_NAV_SECTIONS}, so a
 * renamed or retired section left behind in somebody's browser is ignored rather
 * than folding a section that is not supposed to fold. Wrapped because storage
 * can throw or hand back a stub with no methods (private windows, blocked site
 * data, and this repo's jsdom runner).
 */
function readCollapsed(storage: ScopedStorage): NavSection[] {
  const fallback = [...COLLAPSIBLE_NAV_SECTIONS];
  try {
    const stored = storage.get<unknown>(COLLAPSED_SECTIONS_KEY, fallback);
    if (!Array.isArray(stored)) return fallback;
    return COLLAPSIBLE_NAV_SECTIONS.filter((section) => stored.includes(section));
  } catch {
    return fallback;
  }
}

/** Is `item` the route currently on screen? `/` matches exactly, the rest by prefix. */
function isActiveItem(item: NavItem, pathname: string): boolean {
  return item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
}

export function Sidebar(): React.ReactElement {
  const { can } = usePermissions();
  // The packaging half of the same filter: `can` answers "may this person",
  // `entitled` answers "does this customer's plan include it".
  const entitled = useEntitled();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const identity = useStorageIdentity();

  // Memoised on the FIELDS, not the object: `useStorageIdentity` builds a fresh
  // literal every render, so keying on it would mint a new keyspace per paint.
  const storage = React.useMemo(
    () => createScopedStorage(identity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId],
  );

  const [collapsed, setCollapsed] = React.useState<NavSection[]>(() => readCollapsed(storage));

  // Re-seed when the identity (and therefore the keyspace) changes — a tenant or
  // user switch must read ITS OWN choice, not keep the previous one on screen.
  React.useEffect(() => {
    setCollapsed(readCollapsed(storage));
  }, [storage]);

  const toggleSection = React.useCallback(
    (section: NavSection) => {
      const next = collapsed.includes(section)
        ? collapsed.filter((each) => each !== section)
        : [...collapsed, section];
      setCollapsed(next);
      try {
        // Lands at the storage identity's narrowest level (the user, once signed
        // in), so the choice is this person's and not the browser's.
        storage.set(COLLAPSED_SECTIONS_KEY, next);
      } catch {
        // A remembered fold is a convenience; losing it must not break the rail.
      }
    },
    [collapsed, storage],
  );

  const visible = visibleNavItems(can, entitled);

  return (
    <aside aria-label="Sidebar" className="flex h-full w-60 flex-col border-r border-border bg-card">
      {/*
        Hidden until focused. The `href` is the real mechanism (it moves the
        document's sequential-navigation point, which a scripted `.focus()`
        alone does not), and the handler adds what a fragment jump cannot: an
        explicit focus on a container that isn't natively focusable.
      */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
        onClick={() => {
          const main = document.getElementById(MAIN_CONTENT_ID);
          if (!main) return;
          if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
          main.focus();
        }}
      >
        Skip to main content
      </a>

      <div className="flex items-center gap-2 px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold">
          A
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-foreground">{APP_NAME}</div>
          <div className="text-[11px] text-muted-foreground">{APP_TAGLINE}</div>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-2">
        {NAV_SECTIONS.map((section) => {
          const items = visible.filter((i) => i.section === section);
          if (items.length === 0) return null;

          const collapsible = COLLAPSIBLE_NAV_SECTIONS.includes(section);
          const holdsActive = items.some((item) => isActiveItem(item, pathname));
          // The active route wins over a stored fold, in both directions: a
          // reader who folded Automate and then opened Runs sees Automate open,
          // and their fold comes back the moment they navigate away.
          const expanded = !collapsible || holdsActive || !collapsed.includes(section);
          const headingId = sectionId(section);
          const listId = `${headingId}-items`;
          const Chevron = expanded ? ChevronDown : ChevronRight;

          return (
            <div key={section} className="mb-4">
              {collapsible ? (
                <button
                  type="button"
                  id={headingId}
                  aria-expanded={expanded}
                  aria-controls={listId}
                  // Not `disabled`: the control keeps its place in the tab order
                  // and its state stays readable. It simply declines to fold the
                  // section the reader is standing in, and says why on hover.
                  aria-disabled={holdsActive || undefined}
                  title={
                    holdsActive
                      ? `${section} stays open while you are on one of its pages`
                      : undefined
                  }
                  onClick={() => {
                    if (holdsActive) return;
                    toggleSection(section);
                  }}
                  className={cn(
                    "flex w-full items-center gap-1 rounded-md px-3 py-1 pb-1 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors",
                    holdsActive ? "cursor-default" : "hover:text-foreground",
                  )}
                >
                  <Chevron className="h-3 w-3 shrink-0" aria-hidden />
                  <span>{section}</span>
                  {!expanded && (
                    <span className="ml-auto font-normal normal-case tracking-normal tabular-nums">
                      {items.length}
                    </span>
                  )}
                </button>
              ) : (
                <div
                  id={headingId}
                  className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {section}
                </div>
              )}

              {/* Named from its own heading, so the links arrive in labelled
                  groups rather than as one undifferentiated list. `hidden` (not
                  an unmounted subtree) is what keeps `aria-controls` pointing at
                  something a screen reader can resolve. */}
              <ul
                id={listId}
                hidden={!expanded}
                aria-labelledby={headingId}
                className="space-y-0.5"
              >
                {items.map((item) => {
                  const active = isActiveItem(item, pathname);
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                          active
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
